import { useCallback, useState } from 'react';

/**
 * Tracks which row ids currently have a request in flight, for lists where several rows can each
 * start their own request.
 *
 * A set rather than a single id because the rows share one request runner (a `useMutation`
 * instance, or a plain async call), whose own pending flag only reflects its most recent call.
 * With a single id, starting a second row while the first was still running reported the first as
 * idle, which re-enabled its action and allowed a duplicate request.
 *
 * Add when a request starts and remove when it settles, so every invocation is counted, including
 * a Retry.
 */
export function useInFlightIds() {
  const [ids, setIds] = useState<ReadonlySet<string>>(() => new Set());

  const add = useCallback((id: string) => {
    setIds((current) => new Set(current).add(id));
  }, []);

  const remove = useCallback((id: string) => {
    setIds((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });
  }, []);

  return { ids, add, remove };
}
